// Tab switching, wake lock, and step navigation for the Project Plan Module block

function bindWakeLock(button) {
    if (!button || button.dataset.ppmWakeBound === 'true') {
        return;
    }

    button.dataset.ppmWakeBound = 'true';

    var wakeLock = null;

    function syncWakeState(isActive) {
        button.setAttribute('aria-checked', isActive ? 'true' : 'false');
        button.classList.toggle('is-active', isActive);
    }

    async function releaseWakeLock() {
        if (!wakeLock) {
            syncWakeState(false);
            return;
        }

        try {
            await wakeLock.release();
        } catch (error) {
            console.warn('project-plan-module wakeLock release failed', error);
        } finally {
            wakeLock = null;
            syncWakeState(false);
        }
    }

    async function requestWakeLock() {
        if (!navigator.wakeLock || typeof navigator.wakeLock.request !== 'function') {
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
            syncWakeState(false);
            return;
        }

        try {
            wakeLock = await navigator.wakeLock.request('screen');
            syncWakeState(true);

            wakeLock.addEventListener('release', function () {
                wakeLock = null;
                syncWakeState(false);
            });
        } catch (error) {
            console.warn('project-plan-module wakeLock request failed', error);
            wakeLock = null;
            syncWakeState(false);
        }
    }

    button.addEventListener('click', function () {
        if (wakeLock) {
            releaseWakeLock();
            return;
        }

        requestWakeLock();
    });

    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible' && button.getAttribute('aria-checked') === 'true' && !wakeLock) {
            requestWakeLock();
        }
    });
}

export function initProjectPlanModules(root = document) {
    root.querySelectorAll('.pplan-module:not([data-ppm-bound])').forEach(function (module) {
        module.dataset.ppmBound = 'true';

        const tabs = Array.from(module.querySelectorAll('.ppm-tab'));
        const panels = Array.from(module.querySelectorAll('.ppm-panel'));
        const wakeLockButton = module.querySelector('.ppm-screen-awake-btn');

        function activateTab(nextTab) {
            const targetId = nextTab.getAttribute('aria-controls');

            tabs.forEach(function (tab) {
                const isActive = tab === nextTab;

                tab.classList.toggle('is-active', isActive);
                tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
                tab.setAttribute('tabindex', isActive ? '0' : '-1');
            });

            panels.forEach(function (panel) {
                const isActive = panel.id === targetId;

                panel.classList.toggle('is-active', isActive);
                panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
                panel.hidden = !isActive;
            });
        }

        // ── Tab switching ──────────────────────────────────────────
        tabs.forEach(function (tab) {
            tab.addEventListener('click', function () {
                activateTab(tab);
            });

            // Arrow key navigation within the tab list
            tab.addEventListener('keydown', function (e) {
                const current = tabs.indexOf(tab);
                let next;

                if (e.key === 'ArrowRight') {
                    next = (current + 1) % tabs.length;
                } else if (e.key === 'ArrowLeft') {
                    next = (current - 1 + tabs.length) % tabs.length;
                } else {
                    return;
                }
                e.preventDefault();
                tabs[next].focus();
                activateTab(tabs[next]);
            });
        });

        const activeTab = module.querySelector('.ppm-tab[aria-selected="true"]') || tabs[0];
        if (activeTab) {
            activateTab(activeTab);
        }

        // ── Step navigation ────────────────────────────────────────
        module.querySelectorAll('[data-ppm-steps]').forEach(function (stepsWrapper) {
            const steps = Array.from(stepsWrapper.querySelectorAll('.ppm-step'));
            let current = steps.findIndex(function (step) {
                return step.classList.contains('is-active');
            });

            if (current < 0) {
                current = 0;
            }

            function showStep(index) {
                steps.forEach(function (s, i) {
                    var isActive = i === index;
                    s.classList.toggle('is-active', isActive);
                    s.setAttribute('aria-hidden', isActive ? 'false' : 'true');
                    s.hidden = !isActive;
                });
                current = index;

                stepsWrapper.querySelectorAll('[data-ppm-prev]').forEach(function (btn) {
                    btn.disabled = current === 0;
                    btn.setAttribute('aria-disabled', current === 0 ? 'true' : 'false');
                });
                stepsWrapper.querySelectorAll('[data-ppm-next]').forEach(function (btn) {
                    btn.disabled = current === steps.length - 1;
                    btn.setAttribute('aria-disabled', current === steps.length - 1 ? 'true' : 'false');
                });
            }

            stepsWrapper.addEventListener('click', function (e) {
                if (e.target.closest('[data-ppm-prev]') && current > 0) {
                    showStep(current - 1);
                } else if (e.target.closest('[data-ppm-next]') && current < steps.length - 1) {
                    showStep(current + 1);
                }
            });

            if (steps.length) {
                showStep(current);
            }
        });

        bindWakeLock(wakeLockButton);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
        initProjectPlanModules();
    });
} else {
    initProjectPlanModules();
}
